import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AgentProfile {
  id: string;
  user_id: string;
  display_name: string | null;
  photo_url: string | null;
  bio: string | null;
  headline: string | null;
  specialties: string[] | null;
  service_areas: string[] | null;
  years_experience: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  slug: string | null;
  licensed_year: number | null;
  license_number: string | null;
  accreditations: string[] | null;
  mls_id: string | null;
  email: string | null;
  phone: string | null;
  agent_role: string | null;
  licensed_states: string[] | null;
  lead_capture_url: string | null;
}

export function useAgentBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ["agent", "slug", slug],
    queryFn: async (): Promise<AgentProfile | null> => {
      if (!slug) return null;

      const { data, error } = await supabase
        .from("agents_public")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();

      if (error) {
        console.error("Error fetching agent by slug:", error);
        throw error;
      }

      return data as AgentProfile | null;
    },
    enabled: !!slug,
  });
}

export function useAgentById(id: string | undefined) {
  return useQuery({
    queryKey: ["agent", "id", id],
    queryFn: async (): Promise<AgentProfile | null> => {
      if (!id) return null;

      const { data, error } = await supabase
        .from("agents")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching agent by id:", error);
        throw error;
      }

      return data as AgentProfile | null;
    },
    enabled: !!id,
  });
}

export default useAgentBySlug;
